/**
 * The platform's one "outdated" answer. A missing change timestamp means untouched: the field is
 * only maintained once a note has an audit to go stale. Despite the name it marks an unreviewed
 * change rather than the last edit — the backend drops it when the text goes back to what was audited.
 */

function toMillis(timestamp) {
  const millis = typeof timestamp?.toMillis === 'function' ? timestamp.toMillis() : null;

  return Number.isFinite(millis) ? millis : null;
}

export function isStoredAuditOutdated(sessionActivity) {
  const lastAuditTimestamp = toMillis(sessionActivity?.auditMeta?.lastAuditTimestamp);

  if (lastAuditTimestamp === null) {
    return false;
  }

  const lastNotesChangeTimestamp = toMillis(sessionActivity?.auditMeta?.lastNotesChangeTimestamp);

  return lastNotesChangeTimestamp !== null && lastNotesChangeTimestamp > lastAuditTimestamp;
} // isStoredAuditOutdated()

export function hasCurrentStoredAudit(sessionActivity) {
  return !!sessionActivity?.audit?.verdict && !isStoredAuditOutdated(sessionActivity);
} // hasCurrentStoredAudit()

/**
 * The three cases cannot be collapsed: after a reload only the baseline is left, because the
 * saved-note timestamps cannot move while the note is unsaved.
 *
 * @param {Object} params
 * @param {string|null} params.auditedNotes - Text reviewed by a run in this session, if there was one.
 * @param {string|null} params.baselineNotes - Text present when the surface opened on a stored verdict.
 */
export function isNoteChangedSinceAudit({ currentNotes, auditedNotes, baselineNotes, isStoredOutdated }) {
  if (auditedNotes !== null) {
    return currentNotes !== auditedNotes;
  }

  if (isStoredOutdated) {
    return true;
  }

  return baselineNotes !== null && currentNotes !== baselineNotes;
} // isNoteChangedSinceAudit()
